// src/private/src/pages/public/BookCallPage.jsx
//
// The public discovery-call link, /book with no token: the one we can send
// anyone, like a Calendly link. Step 1 picks times (up to 2 in request
// mode, one in direct mode); step 2 says who's booking. A brand-new email
// lands on /book/<token>, which shows the request (or booked call). An email
// we already have never gets its token back on screen, since it unlocks
// their booking: their times are filed and the emails carry the link.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';

import {
  fetchPublicAvailability,
  fetchPublicBookingContext,
  submitPublicBooking,
} from '@src/api/clientSchedule';
import { dayjs } from '@src/corelib';
import { SlotPicker, formatOfficeHours, resolveBrowserTimezone } from './ProspectSchedulePage';

const BRAND_ORANGE = '#ff680d';
const HEADER_BG = '#111827';
const LOGO_URL =
  'https://leadguruspro.s3.amazonaws.com/static/img/lead-gurus-logo-full-dark.png';
const ORANGE_BUTTON = {
  bgcolor: BRAND_ORANGE,
  backgroundImage: 'none',
  '&:hover': { bgcolor: '#e05a08', backgroundImage: 'none' },
  py: 1.25,
  fontWeight: 600,
};

const EMPTY = { name: '', email: '', company: '', phone: '', notes: '', website: '' };

export default function BookCallPage() {
  const navigate = useNavigate();
  const browserTz = resolveBrowserTimezone();

  const [context, setContext] = useState(null);
  const [loadState, setLoadState] = useState('loading'); // loading | ready | error
  const [slots, setSlots] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [picked, setPicked] = useState([]);
  const [step, setStep] = useState('times'); // times | details | done
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [doneOutcome, setDoneOutcome] = useState(null);

  const isDirect = context?.mode === 'direct';
  const maxPicks = isDirect ? 1 : context?.max_requested_times || 2;

  const loadSlots = () =>
    fetchPublicAvailability()
      // api.request resolves with the full axios response — unwrap .data.
      .then((res) => {
        const list = (res.data || []).map((s) => s.starts_at);
        setSlots(list);
        setSelectedDay((day) => (day && list.some((iso) => dayjs(iso).format('YYYY-MM-DD') === day)
          ? day
          : list.length ? dayjs(list[0]).format('YYYY-MM-DD') : null));
      });

  useEffect(() => {
    Promise.all([fetchPublicBookingContext(), loadSlots()])
      .then(([ctx]) => {
        setContext(ctx.data);
        setLoadState('ready');
      })
      .catch(() => setLoadState('error'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const slotsByDay = useMemo(() => {
    const map = {};
    for (const iso of slots) {
      const key = dayjs(iso).format('YYYY-MM-DD');
      (map[key] = map[key] || []).push(iso);
    }
    return map;
  }, [slots]);
  const availableDays = useMemo(() => Object.keys(slotsByDay), [slotsByDay]);

  const togglePick = (iso) => {
    setPicked((prev) => {
      if (prev.includes(iso)) return prev.filter((x) => x !== iso);
      if (maxPicks === 1) return [iso];
      if (prev.length >= maxPicks) return prev;
      return [...prev, iso].sort();
    });
  };

  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
  const ready = form.name.trim() && form.email.trim() && form.company.trim();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!ready || submitting || !picked.length) return;
    setSubmitting(true);
    setError('');
    submitPublicBooking({ ...form, times: picked })
      .then((res) => {
        if (res.data?.token) {
          navigate(`/book/${res.data.token}`);
          return;
        }
        setDoneOutcome(res.data?.outcome || 'requested');
        setStep('done');
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 409) {
          // A picked time was just taken: back to the picker with fresh slots.
          setPicked([]);
          setStep('times');
          loadSlots().catch(() => {});
          setError(err?.response?.data?.detail || 'One of those times was just taken. Please pick again.');
          return;
        }
        setError(
          status === 429
            ? 'Too many tries. Please wait a bit and try again.'
            : err?.response?.data?.detail || 'Something went wrong. Please try again.',
        );
      })
      .finally(() => setSubmitting(false));
  };

  const officeHoursCopy = formatOfficeHours(context?.office_hours);
  const pickedChips = picked.map((iso) => (
    <Chip
      key={iso}
      label={dayjs(iso).format('ddd, MMM D · h:mm A')}
      onDelete={step === 'times' ? () => togglePick(iso) : undefined}
      sx={{ bgcolor: BRAND_ORANGE, color: '#fff', '& .MuiChip-deleteIcon': { color: '#fff' } }}
    />
  ));

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f4f4', py: { xs: 2, sm: 4 } }}>
      <Box sx={{ maxWidth: 640, mx: 'auto', px: 2 }}>
        <Card sx={{ overflow: 'hidden', borderRadius: 2 }}>
          <Box sx={{ bgcolor: HEADER_BG, color: '#fff', p: 3, textAlign: 'center' }}>
            <img
              src={LOGO_URL}
              alt="Lead Gurus"
              style={{ display: 'block', maxHeight: 32, margin: '0 auto 12px' }}
            />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Schedule a Call with Lead Gurus
            </Typography>
          </Box>

          <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
            {loadState === 'loading' && (
              <Stack spacing={2}>
                <Skeleton variant="rounded" height={56} />
                <Skeleton variant="rounded" height={120} />
              </Stack>
            )}

            {loadState === 'error' && (
              <Alert severity="error">
                Something went wrong loading this page. Please refresh, or email{' '}
                <a href="mailto:getstarted@leadgurus.com">getstarted@leadgurus.com</a>.
              </Alert>
            )}

            {loadState === 'ready' && step === 'done' && (
              <Stack spacing={1.5} alignItems="center" sx={{ textAlign: 'center', py: 2 }}>
                <MarkEmailReadIcon color="success" sx={{ fontSize: 40 }} />
                <Typography variant="h6">
                  {doneOutcome === 'already_booked' ? 'You already have a call booked' : 'Got it, check your email'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {doneOutcome === 'already_booked' ? (
                    <>
                      We sent your scheduling link to <strong>{form.email}</strong>. Use it to see
                      or change the call you have booked.
                    </>
                  ) : doneOutcome === 'booked' ? (
                    <>Your call is booked. The details are on their way to <strong>{form.email}</strong>.</>
                  ) : (
                    <>
                      We got your times. We&apos;ll confirm one within 24 hours and send the calendar
                      invite to <strong>{form.email}</strong>.
                    </>
                  )}
                </Typography>
              </Stack>
            )}

            {loadState === 'ready' && step !== 'done' && (
              <>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                  <EventAvailableIcon sx={{ color: BRAND_ORANGE }} />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {context.duration_minutes}-minute call with the Lead Gurus team
                    </Typography>
                    {officeHoursCopy && (
                      <Typography variant="caption" color="text.secondary">
                        Call hours: {officeHoursCopy}
                      </Typography>
                    )}
                  </Box>
                </Stack>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                  </Alert>
                )}

                {step === 'times' && (
                  <>
                    {!isDirect && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Pick up to {maxPicks} times that work for you. We&apos;ll confirm one
                        within 24 hours and send the calendar invite.
                      </Alert>
                    )}
                    <SlotPicker
                      loading={false}
                      slotsByDay={slotsByDay}
                      availableDays={availableDays}
                      selectedDay={selectedDay}
                      onSelectDay={setSelectedDay}
                      onSelectSlot={togglePick}
                      picked={picked}
                      browserTz={browserTz}
                    />
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="overline" color="text.secondary">
                        {isDirect ? 'Your time' : `Your picks (${picked.length}/${maxPicks})`}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5, mb: 2 }}>
                        {picked.length === 0 && (
                          <Typography variant="body2" color="text.secondary">
                            Tap a time above to add it.
                          </Typography>
                        )}
                        {pickedChips}
                      </Stack>
                      <Button
                        variant="contained"
                        fullWidth
                        disabled={!picked.length}
                        onClick={() => setStep('details')}
                        sx={ORANGE_BUTTON}
                      >
                        Next
                      </Button>
                    </Box>
                  </>
                )}

                {step === 'details' && (
                  <Box component="form" onSubmit={handleSubmit} noValidate>
                    <Typography variant="overline" color="text.secondary">
                      {isDirect ? 'Your time' : 'Your picks'}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mt: 0.5, mb: 2 }}>
                      {pickedChips}
                      <Button size="small" onClick={() => setStep('times')} sx={{ color: BRAND_ORANGE }}>
                        Change
                      </Button>
                    </Stack>
                    <Divider sx={{ mb: 2 }} />
                    <Stack spacing={2}>
                      <TextField label="Your name" value={form.name} onChange={set('name')} required fullWidth autoFocus />
                      <TextField label="Email" type="email" value={form.email} onChange={set('email')} required fullWidth />
                      <TextField label="Company" value={form.company} onChange={set('company')} required fullWidth />
                      <TextField label="Phone" type="tel" value={form.phone} onChange={set('phone')} fullWidth />
                      <TextField
                        label="Anything you'd like us to know? (optional)"
                        multiline
                        minRows={2}
                        value={form.notes}
                        onChange={set('notes')}
                        fullWidth
                      />
                      {/* Honeypot: hidden from people, bots fill it in. */}
                      <Box sx={{ position: 'absolute', left: -9999, width: 1, height: 1, overflow: 'hidden' }} aria-hidden>
                        <input tabIndex={-1} autoComplete="off" name="website" value={form.website} onChange={set('website')} />
                      </Box>
                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={!ready || submitting}
                        sx={ORANGE_BUTTON}
                        startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
                      >
                        {submitting ? 'Sending…' : isDirect ? 'Book it' : 'Request these times'}
                      </Button>
                    </Stack>
                  </Box>
                )}
              </>
            )}
          </CardContent>

          <Divider />
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Lead Gurus ·{' '}
              <a href="https://leadgurus.com" style={{ color: 'inherit' }}>
                leadgurus.com
              </a>
            </Typography>
          </Box>
        </Card>
      </Box>
    </Box>
  );
}
